import api, { handleResponse, handleError } from './api';

/**
 * Per-(type, channel) on/off switches for notifications. Channel is either
 * 'push' or 'email'. In-app feed is always on (it IS the inbox).
 *
 * Backend default for missing rows is enabled, so the GET returns a full
 * catalog of every NotificationType with the resolved boolean — the UI
 * doesn't need to know which ones the user has actually touched.
 */
const notificationPreferenceService = {
  async list() {
    try {
      const r = await api.get('/notification-preferences');
      return handleResponse(r);
    } catch (e) { return handleError(e); }
  },

  async update(type, channel, enabled) {
    try {
      const r = await api.put('/notification-preferences', { type, channel, enabled });
      return handleResponse(r);
    } catch (e) { return handleError(e); }
  },

  /**
   * The recurring event digest. Separate from the per-type switches above
   * because it isn't a NotificationType — it's its own scheduled send with a
   * cadence. Returns the current setting, where the next send would be for
   * ({ lat, lng, city } — the location pill on the last tracked page view) and
   * the catalog of cadences, so the picker can render in one round trip.
   */
  async getDigest() {
    try {
      const r = await api.get('/notification-preferences/digest');
      return handleResponse(r);
    } catch (e) { return handleError(e); }
  },

  /**
   * cadence is 'Off' | 'Daily' | 'Weekly' | 'Biweekly' | 'Monthly'. `point` is the
   * location pill's { lat, lng } when known — the server's fallback for someone the
   * tracker has never placed; the digest itself follows where they are.
   */
  async updateDigest(cadence, point = null) {
    try {
      const lat = point?.lat ?? null;
      const lng = point?.lng ?? null;
      const r = await api.put('/notification-preferences/digest', { cadence, lat, lng });
      return handleResponse(r);
    } catch (e) { return handleError(e); }
  },

  /**
   * Broadcast/newsletter opt-out. Governs marketing only — turning it off never
   * suppresses a receipt, a ticket, or any other transactional email.
   */
  async updateMarketing(enabled) {
    try {
      const r = await api.put('/notification-preferences/marketing', { enabled });
      return handleResponse(r);
    } catch (e) { return handleError(e); }
  },
};

export default notificationPreferenceService;
